import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Megaphone, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError, apiGet } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useAdMutations, useAdSources, type AdSource } from './api';

const PLATFORMS: Record<AdSource['platform'], { label: string; colour: string; steps: string[] }> = {
  meta: {
    label: 'Facebook & Instagram lead ads',
    colour: '#0866FF',
    steps: [
      'Connect the Facebook page in Inbox → Channels (the same page your ads run from).',
      'In your Meta app, give the page token the leads_retrieval permission and subscribe the Page webhook to “leadgen”.',
      'Choose that page here. New leads from every lead form on it arrive in Incoming within seconds.',
    ],
  },
  google: {
    label: 'Google Ads lead forms',
    colour: '#EA4335',
    steps: [
      'Google Ads → your lead form asset → Lead delivery → Webhook integration.',
      'Paste the webhook URL and the key below, then click “Send test data”.',
    ],
  },
  tiktok: {
    label: 'TikTok lead generation',
    colour: '#111111',
    steps: ['TikTok Ads Manager → Tools → Leads Center → CRM integration → Webhook.', 'Paste the webhook URL below (the token is already in it).'],
  },
};

/** Lead ads (AmoCRM and HubSpot ads integrations). */
export function AdsPage() {
  const { data: sources = [], isLoading } = useAdSources();
  const [editing, setEditing] = useState<AdSource | 'new' | null>(null);
  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value).catch(() => undefined);
    toast.success('Copied');
  };

  return (
    <div>
      <PageHeader
        title="Lead ads"
        description="Leads from Facebook, Instagram, Google and TikTok ad forms land in Incoming straight away, tagged with the campaign — no CSV downloads."
        breadcrumbs={[{ label: 'Marketing hub' }, { label: 'Lead ads' }]}
        actions={
          <Can permission="marketing.ads.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> Connect lead ads
            </Button>
          </Can>
        }
      />
      {isLoading ? (
        <PageLoader />
      ) : sources.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <Megaphone className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No lead ads connected</p>
          <p className="mt-1 text-sm text-muted-foreground">Connect Meta, Google or TikTok and every ad lead becomes a lead here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sources.map((source) => {
            const platform = PLATFORMS[source.platform];
            return (
              <Card key={source.id}>
                <CardHeader className="flex-row items-start justify-between space-y-0">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ backgroundColor: platform.colour }}>
                      <Megaphone className="h-5 w-5" />
                    </span>
                    <div>
                      <CardTitle className="text-base">{source.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {platform.label} · {source.leadCount} leads{source.lastLeadAt ? ` · last ${formatDateTime(source.lastLeadAt)}` : ''}
                      </p>
                      {source.lastError && <p className="mt-1 text-xs text-destructive">{source.lastError}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={source.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>{source.isActive ? 'On' : 'Off'}</Badge>
                    <Can permission="marketing.ads.update">
                      <Button size="sm" variant="outline" onClick={() => setEditing(source)}>
                        Settings
                      </Button>
                    </Can>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {source.webhookUrl && (
                    <div className="space-y-1">
                      <Label className="text-xs">Webhook URL</Label>
                      <div className="flex gap-2">
                        <code className="min-w-0 flex-1 truncate rounded-lg bg-muted/60 px-3 py-2 text-xs">{source.webhookUrl}</code>
                        <Button size="sm" variant="outline" onClick={() => copy(source.webhookUrl!)}>
                          <Copy />
                        </Button>
                      </div>
                    </div>
                  )}
                  {source.key && (
                    <div className="space-y-1">
                      <Label className="text-xs">Key</Label>
                      <div className="flex gap-2">
                        <code className="min-w-0 flex-1 truncate rounded-lg bg-muted/60 px-3 py-2 text-xs">{source.key}</code>
                        <Button size="sm" variant="outline" onClick={() => copy(source.key!)}>
                          <Copy />
                        </Button>
                      </div>
                    </div>
                  )}
                  <ol className="list-decimal space-y-1 rounded-xl bg-primary/[0.04] p-4 ps-8 text-sm text-muted-foreground">
                    {platform.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {editing && <SourceDialog source={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SourceDialog({ source, onClose }: { source: AdSource | null; onClose: () => void }) {
  const { save, remove } = useAdMutations();
  const { data: users = [] } = useUserOptions();
  const { data: pages = [] } = useQuery({
    queryKey: ['growth', 'social', 'accounts'],
    queryFn: () => apiGet<{ id: string; kind: string; name: string }[]>('/growth/social/accounts').catch(() => []),
  });
  const [platform, setPlatform] = useState<AdSource['platform']>(source?.platform ?? 'meta');
  const [name, setName] = useState(source?.name ?? 'Facebook lead ads');
  const [channelId, setChannelId] = useState(source?.channelId ?? '');
  const [ownerId, setOwnerId] = useState(source?.ownerId ?? '');
  const [unsorted, setUnsorted] = useState(source?.unsorted ?? true);
  const [isActive, setIsActive] = useState(source?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);
  const facebookPages = pages.filter((page) => page.kind === 'FACEBOOK');

  const submit = () =>
    save.mutate(
      { id: source?.id, platform, name, channelId: channelId || null, ownerId: ownerId || null, unsorted, isActive },
      { onSuccess: () => { toast.success('Saved'); onClose(); }, onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not save') },
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{source ? 'Lead ads settings' : 'Connect lead ads'}</DialogTitle>
          <DialogDescription>Leads arrive as incoming leads, owned by whoever you choose.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Network</Label>
            <Select
              value={platform}
              disabled={Boolean(source)}
              onChange={(event) => {
                const value = event.target.value as AdSource['platform'];
                setPlatform(value);
                setName(PLATFORMS[value].label);
              }}
            >
              {Object.entries(PLATFORMS).map(([value, item]) => (
                <option key={value} value={value}>
                  {item.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          {platform === 'meta' && (
            <div className="space-y-1">
              <Label>Facebook page</Label>
              <Select value={channelId} onChange={(event) => setChannelId(event.target.value)}>
                <option value="">Choose…</option>
                {facebookPages.map((page) => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </Select>
              {!facebookPages.length && (
                <Link to="/inbox/channels" className="text-xs text-primary underline">
                  Connect a Facebook page first
                </Link>
              )}
            </div>
          )}
          <div className="space-y-1">
            <Label>Give leads to</Label>
            <Select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
              <option value="">Sales managers decide</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={unsorted} onCheckedChange={setUnsorted} /> Wait in Incoming to be accepted
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isActive} onCheckedChange={setIsActive} /> On
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter className={source ? 'sm:justify-between' : undefined}>
          {source && (
            <Button variant="ghost" className="text-destructive" onClick={() => window.confirm('Disconnect?') && remove.mutate(source.id, { onSuccess: onClose })}>
              <Trash2 /> Remove
            </Button>
          )}
          <Button onClick={submit} disabled={save.isPending || name.trim().length < 2}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
